import { useTheme } from "next-themes";
import useImageUpload from "../hooks/useImageUpload";
import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import ImageWithLoader from "./imageLoad";

const ImageUpload = forwardRef(({ initialImage, onFileChange, onError, disabled = false, allowedFileTypes }, ref) => {
  const { previewUrl, file, fileInputRef, handleThumbnailClick, handleRemove, handleChange, handleDrop, handleDragOver } = useImageUpload({ initialImage, allowedTypes: allowedFileTypes });
  const [imageError, setImageError] = useState(false);
  const { t } = useTranslation();

  const { theme } = useTheme();

  const displayFileName = file ? file.name : previewUrl ? previewUrl.split("/").pop() : "";

  useImperativeHandle(ref, () => ({
    reset: () => handleRemove(),
    getFile: () => file,
  }), [handleRemove, file]);

  const handleImgError = () => {
    setImageError(true);
    onError?.("Failed to load image preview");
  };

  useEffect(() => {
    if (onFileChange) onFileChange(file);
  }, [file, onFileChange]);

  useEffect(() => {
    setImageError(false);
  }, [previewUrl]);

  useEffect(() => {
    if (disabled && !initialImage) {
      //   setImageError(false);
      handleRemove();
    }
  }, [disabled, initialImage, handleRemove]);

  if (disabled) {
    if (!initialImage || imageError) {
      return (
        <div
          className='rounded p-5 text-center text-danger'
          style={{
            backgroundColor: theme === "dark" ? "rgb(71, 71, 71)" : "",
            border: "1px solid",
            borderColor: theme === "dark" ? "rgb(64, 61, 64)" : "",
            overflow: "hidden",
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width={24}
            height={24}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth={1.75}
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-alert-square-rounded'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z' />
            <path d='M12 8v4' />
            <path d='M12 16h.01' />
          </svg>
          <p
            className='m-0'
            style={{
              color: theme === "dark" ? "rgb(171, 171, 171)" : "rgb(101, 101, 101)",
            }}
          >
            {t("modals.request.failed")}
          </p>
        </div>
      );
    }

    return (
      <div>
        {previewUrl && !imageError && (
          // <img src={previewUrl} loading='lazy' alt='Preview' className='w-100' onError={handleImgError} style={{ objectFit: "contain", display: "block" }} />
          <ImageWithLoader src={previewUrl} />
        )}
        {/* {imageError && (
          <div className='border rounded p-3 text-center text-danger'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            <span>{t("modals.request.error")}</span>
          </div>
        )} */}
        {/* {displayFileName && (
          <div
            className='d-flex align-items-center mt-2 rounded p-2'
            style={{ border: "1px solid", borderColor: theme === "dark" ? "rgb(99, 99, 99)" : "rgb(149, 149, 149)", backgroundColor: theme === "dark" ? "rgb(35, 35, 35)" : "rgb(219, 219, 219)" }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              className='lucide lucide-file-image-icon lucide-file-image'
              viewBox='0 0 24 24'
              style={{
              color: theme === "dark" ? "rgb(160,160,160)" : "rgb(169, 78, 195)",
              }}
            >
              <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'></path>
              <path d='M14 2v4a2 2 0 0 0 2 2h4'></path>
              <circle cx='10' cy='12' r='2'></circle>
              <path d='m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22'></path>
            </svg>
            <span className='flex-grow-1 text-truncate ms-2' title={displayFileName} style={{ color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(59, 59, 59)" }}>
              {displayFileName}
            </span>
          </div>
        )} */}
      </div>
    );
  }

  return (
    <div>
      <div
        className='rounded position-relative'
        onClick={handleThumbnailClick}
        aria-label='Image upload dropzone'
        tabIndex={0}
        // onDrop={handleDrop}
        // onDragOver={handleDragOver}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDrop(e);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDragOver(e);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{
          cursor: "pointer",
          backgroundColor: theme === "dark" ? "rgb(71, 71, 71)" : "rgb(219, 219, 219)",
          border: "1px solid",
          borderColor: theme === "dark" ? "rgb(64, 61, 64)" : "rgb(151, 151, 151)",
          overflow: "hidden",
        }}
      >
        {previewUrl && !imageError ? (
          <img src={previewUrl} loading='lazy' alt='Preview' className='w-100' onError={handleImgError} style={{ objectFit: "contain", display: "block", maxHeight: '800px' }} />
        ) : previewUrl && imageError ? (
          <div className='rounded p-5 text-center text-danger'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={24}
              height={24}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={1.75}
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-alert-square-rounded'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z' />
              <path d='M12 8v4' />
              <path d='M12 16h.01' />
            </svg>
            <p
              className='m-0'
              style={{
                color: theme === "dark" ? "rgb(171, 171, 171)" : "rgb(101, 101, 101)",
              }}
            >
              {t("modals.request.failed")}
            </p>
          </div>
        ) : (
          <div className='d-flex flex-column align-items-center justify-content-center' style={{ height: "200px", color: theme === "dark" ? "rgb(170, 170, 170)" : "rgb(119, 119, 119)" }}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={34}
              height={34}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={1.25}
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-big-up-line'
              style={{
                color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(221, 108, 229)",
              }}
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M9 12h-3.586a1 1 0 0 1 -.707 -1.707l6.586 -6.586a1 1 0 0 1 1.414 0l6.586 6.586a1 1 0 0 1 -.707 1.707h-3.586v6h-6v-6z' />
              <path d='M9 21h6' />
            </svg>
            <p className='mt-2 mb-0'>{t("modals.request.image")}</p>
          </div>
        )}

        {!imageError && previewUrl && (
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
            style={{ backgroundColor: "rgba(0,0,0,0.5)", opacity: 0, transition: "opacity 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
          >
            <button
              type='button'
              className='btn btn-light btn-sm me-2'
              style={{
                padding: ".4rem .5rem",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleThumbnailClick();
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={26}
                height={26}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={1.5}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-upload'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                <path d='M7 9l5 -5l5 5' />
                <path d='M12 4l0 12' />
              </svg>
            </button>
            <button
              type='button'
              className='btn btn-danger btn-sm'
              style={{
                padding: ".4rem .5rem",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width={26}
                height={26}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth={1.25}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-trash'
              >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M4 7l16 0' />
                <path d='M10 11l0 6' />
                <path d='M14 11l0 6' />
                <path d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12' />
                <path d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3' />
              </svg>
            </button>
          </div>
        )}

        <input type='file' accept='image/*' ref={fileInputRef} onChange={handleChange} style={{ display: "none" }} />
      </div>

      {file && (
        <div
          className='d-flex align-items-center mt-2 rounded p-2'
          style={{ border: "1px solid", borderColor: theme === "dark" ? "rgb(99, 99, 99)" : "rgb(149, 149, 149)", backgroundColor: theme === "dark" ? "rgb(35, 35, 35)" : "rgb(219, 219, 219)" }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            fill='none'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='2'
            className='lucide lucide-file-image-icon lucide-file-image'
            viewBox='0 0 24 24'
            style={{
              color: theme === "dark" ? "rgb(160,160,160)" : "rgb(169, 78, 195)",
            }}
          >
            <path d='M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z'></path>
            <path d='M14 2v4a2 2 0 0 0 2 2h4'></path>
            <circle cx='10' cy='12' r='2'></circle>
            <path d='m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22'></path>
          </svg>
          <span className='flex-grow-1 text-truncate ms-2' title={file.name} style={{ color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(59, 59, 59)" }}>
            {file.name}
          </span>
          <button type='button' className='btn btn-link p-0 ms-2' style={{ textDecoration: "none", color: theme === "dark" ? "rgb(180,180,180)" : "rgb(80, 80, 80)" }} onClick={handleRemove}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={24}
              height={24}
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth={1.5}
              strokeLinecap='round'
              strokeLinejoin='round'
              className='icon icon-tabler icons-tabler-outline icon-tabler-x'
            >
              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
              <path d='M18 6l-12 12' />
              <path d='M6 6l12 12' />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
});

export default ImageUpload;
